import { router, useForm } from "@inertiajs/react";
import { Loader2, Upload, X } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { SocialFeedMockup } from "@/components/onboarding/social-feed-mockup";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";
import { startOnboardingBrandingPath } from "@/routes";

interface DomainInfo {
  id: number;
  hostname: string;
  customLogoUrl?: string | null;
  bgColorHex?: string | null;
  textColorHex?: string | null;
  verificationStatus?: string;
}

interface LogoCandidate {
  url: string;
  source: string;
}

interface BrandingDraftPayload {
  bgColorHex: string;
  textColorHex: string;
  selectedLogoUrl: string | null;
  customLogo: File | null;
  ogTitle: string;
  ogDescription: string;
  heroImageUrl: string;
}

interface BrandingExtraction {
  id: number;
  status: "pending" | "processing" | "completed" | "failed";
  stage: string;
  logoCandidates: LogoCandidate[];
  suggestedBgColorHex?: string | null;
  suggestedTextColorHex?: string | null;
  draftBgColorHex?: string | null;
  draftTextColorHex?: string | null;
  draftLogoUrl?: string | null;
  previewImageUrl?: string | null;
  previewStatus?: string | null;
  errorMessage?: string | null;
  mainPageTitle?: string | null;
  mainPageDescription?: string | null;
}

interface BrandingStepProps {
  domain: DomainInfo | null;
  extraction: BrandingExtraction | null;
  onPostClick: (payload: BrandingDraftPayload) => void;
}

function normalizeHex(value: string) {
  const trimmed = value.trim();
  if (!trimmed) return "";
  const normalized = trimmed.startsWith("#") ? trimmed : `#${trimmed}`;
  return /^[0-9a-f]{6}$/i.test(normalized.slice(1)) ? normalized.toUpperCase() : normalized;
}

function stageLabel(stage?: string) {
  switch (stage) {
    case "pending":
      return "Starting…";
    case "scraping":
      return "Scanning your homepage…";
    case "logos":
      return "Finding logo options…";
    case "colors":
      return "Extracting brand colors…";
    case "preview":
    case "preview_rendering":
      return "Rendering a sample…";
    case "completed":
      return "Ready";
    case "failed":
      return "Failed";
    default:
      return "Working…";
  }
}

export function BrandingStep({ domain, extraction, onPostClick }: BrandingStepProps) {
  // eslint-disable-next-line @typescript-eslint/unbound-method
  const { data, setData, setDefaults } = useForm(`BrandingStep:${domain?.id ?? ""}`, {
    bgColorHex: "",
    textColorHex: "",
    selectedLogoUrl: null as string | null,
    ogTitle: "",
    ogDescription: "",
    heroImageUrl: "",
  });

  const [uploadedLogo, setUploadedLogo] = useState<File | null>(null);
  const [uploadedLogoName, setUploadedLogoName] = useState("");
  const [uploadedLogoPreview, setUploadedLogoPreview] = useState<string | null>(null);

  const [hasTouchedColors, setHasTouchedColors] = useState(() => data.bgColorHex !== "" || data.textColorHex !== "");
  const [hasTouchedLogoSelection, setHasTouchedLogoSelection] = useState(() => data.selectedLogoUrl !== null);
  const [hasTouchedContent, setHasTouchedContent] = useState(
    () => data.ogTitle !== "" || data.ogDescription !== "" || data.heroImageUrl !== "",
  );
  const [isStarting, setIsStarting] = useState(false);
  const [activeFeed, setActiveFeed] = useState("x");
  const pollStartedAt = useRef(Date.now());

  const initializedFromExtraction = useRef(false);

  const rootHost = useMemo(() => domain?.hostname?.replace(/^\*\./, "") ?? "", [domain]);

  const startExtraction = useCallback(() => {
    if (!domain) return;
    setIsStarting(true);
    router.post(
      startOnboardingBrandingPath(),
      { domain_id: domain.id },
      {
        preserveState: true,
        preserveUrl: true,
        onFinish: () => setIsStarting(false),
      },
    );
  }, [domain]);

  useEffect(() => {
    if (!domain) return;
    if (!extraction || extraction.status === "pending" || extraction.status === "failed") {
      startExtraction();
    }
  }, []);

  const MAX_POLL_DURATION_MS = 3 * 60 * 1000;

  const shouldPoll = useMemo(() => {
    const elapsed = Date.now() - pollStartedAt.current;
    if (elapsed > MAX_POLL_DURATION_MS) return false;

    if (!domain) return false;
    if (!extraction) return true;
    if (extraction.status === "processing" && !["preview", "preview_rendering"].includes(extraction.stage)) return true;
    if (extraction.status === "processing" && ["preview", "preview_rendering"].includes(extraction.stage)) {
      if (!extraction.previewImageUrl) return true;
    }
    if (extraction.previewStatus && !["completed", "failed"].includes(extraction.previewStatus)) return true;
    return false;
  }, [domain, extraction]);

  useEffect(() => {
    if (!shouldPoll) return;
    const interval = window.setInterval(() => {
      router.reload({ only: ["extraction", "onboarding", "domain"] });
    }, 1500);
    return () => window.clearInterval(interval);
  }, [shouldPoll]);

  useEffect(() => {
    if (!extraction || initializedFromExtraction.current) return;
    setDefaults({
      bgColorHex:
        data.bgColorHex || extraction.draftBgColorHex || extraction.suggestedBgColorHex || domain?.bgColorHex || "",
      textColorHex:
        data.textColorHex ||
        extraction.draftTextColorHex ||
        extraction.suggestedTextColorHex ||
        domain?.textColorHex ||
        "",
      selectedLogoUrl: data.selectedLogoUrl || extraction.draftLogoUrl || extraction.logoCandidates?.[0]?.url || null,
      ogTitle: data.ogTitle || extraction.mainPageTitle || "",
      ogDescription: data.ogDescription || extraction.mainPageDescription || "",
    });
    initializedFromExtraction.current = true;
  }, [extraction, domain]);

  useEffect(() => {
    if (!extraction) return;
    if (!hasTouchedColors) {
      if (!data.bgColorHex && extraction.suggestedBgColorHex) setData("bgColorHex", extraction.suggestedBgColorHex);
      if (!data.textColorHex && extraction.suggestedTextColorHex)
        setData("textColorHex", extraction.suggestedTextColorHex);
    }
    if (!hasTouchedLogoSelection && !uploadedLogo) {
      if (!data.selectedLogoUrl && extraction.draftLogoUrl) setData("selectedLogoUrl", extraction.draftLogoUrl);
      else if (!data.selectedLogoUrl && extraction.logoCandidates?.[0]?.url)
        setData("selectedLogoUrl", extraction.logoCandidates[0].url);
    }
    if (!hasTouchedContent) {
      if (!data.ogTitle && extraction.mainPageTitle) setData("ogTitle", extraction.mainPageTitle);
      if (!data.ogDescription && extraction.mainPageDescription)
        setData("ogDescription", extraction.mainPageDescription);
    }
  }, [extraction]);

  const previewBg = useMemo(() => {
    const n = normalizeHex(data.bgColorHex);
    return /^#[0-9A-F]{6}$/.test(n) ? n : "#0B1220";
  }, [data.bgColorHex]);

  const previewText = useMemo(() => {
    const n = normalizeHex(data.textColorHex);
    return /^#[0-9A-F]{6}$/.test(n) ? n : "#FFFFFF";
  }, [data.textColorHex]);

  const handleLogoCandidateSelect = (url: string) => {
    setHasTouchedLogoSelection(true);
    setData("selectedLogoUrl", url);
    setUploadedLogo(null);
    setUploadedLogoName("");
    setUploadedLogoPreview(null);
  };

  const handleLogoUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setHasTouchedLogoSelection(true);
    setUploadedLogo(file);
    setUploadedLogoName(file.name);
    const reader = new FileReader();
    reader.onloadend = () => setUploadedLogoPreview(reader.result as string);
    reader.readAsDataURL(file);
  };

  const handleRemoveUpload = () => {
    setUploadedLogo(null);
    setUploadedLogoName("");
    setUploadedLogoPreview(null);
  };

  const brandDataReady = extraction != null && ["preview", "preview_rendering", "completed"].includes(extraction.stage);
  const isExtractionLoading = isStarting || !extraction || (extraction.status === "processing" && !brandDataReady);

  const currentLogoUrl = uploadedLogoPreview || data.selectedLogoUrl || null;
  const shouldUseLiveMockupPreview =
    hasTouchedColors || hasTouchedLogoSelection || hasTouchedContent || Boolean(uploadedLogoPreview);
  const mockupPreviewImageUrl = shouldUseLiveMockupPreview ? null : (extraction?.previewImageUrl ?? null);

  const handlePostClick = useCallback(() => {
    onPostClick({
      bgColorHex: normalizeHex(data.bgColorHex),
      textColorHex: normalizeHex(data.textColorHex),
      selectedLogoUrl: data.selectedLogoUrl,
      customLogo: uploadedLogo,
      ogTitle: data.ogTitle,
      ogDescription: data.ogDescription,
      heroImageUrl: data.heroImageUrl,
    });
  }, [data, onPostClick, uploadedLogo]);

  return (
    <div className="w-full">
      <div className="mb-5 text-center sm:mb-8">
        <h1 className="text-xl font-bold tracking-tight text-neutral-900 sm:text-2xl lg:text-3xl dark:text-white">
          Customize your brand
        </h1>
        <p className="mt-1.5 text-xs text-neutral-500 sm:mt-2 sm:text-sm dark:text-neutral-400">
          {isExtractionLoading
            ? `Scanning ${rootHost} for your brand…`
            : "Tweak your logo and colors, then see how your links will look on social media."}
        </p>
      </div>

      <div className="grid gap-5 sm:gap-8 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.3fr)]">
        {/* Left column: Brand controls */}
        <div className="space-y-4 sm:space-y-6">
          {/* OG Content */}
          <div className="rounded-2xl border border-neutral-200 bg-white p-4 shadow-sm sm:p-5 dark:border-neutral-800 dark:bg-neutral-900">
            <h3 className="mb-3 text-sm font-semibold text-neutral-900 sm:mb-4 dark:text-white">OG Content</h3>
            {isExtractionLoading ? (
              <div className="space-y-3">
                <Skeleton className="h-10 w-full" />
                <Skeleton className="h-16 w-full" />
                <Skeleton className="h-10 w-full" />
                <div className="flex items-center gap-2 text-xs text-neutral-400">
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  {extraction ? stageLabel(extraction.stage) : "Extracting page info…"}
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="space-y-1.5">
                  <Label className="text-xs" htmlFor="og-title">
                    Title
                  </Label>
                  <Input
                    id="og-title"
                    value={data.ogTitle}
                    placeholder="Your page title"
                    onChange={(e) => {
                      setHasTouchedContent(true);
                      setData("ogTitle", e.target.value);
                    }}
                    className="text-sm"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs" htmlFor="og-description">
                    Description
                  </Label>
                  <Textarea
                    id="og-description"
                    value={data.ogDescription}
                    placeholder="A short description of your page"
                    rows={2}
                    onChange={(e) => {
                      setHasTouchedContent(true);
                      setData("ogDescription", e.target.value);
                    }}
                    className="resize-none text-sm"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs" htmlFor="hero-image-url">
                    Hero Image URL
                  </Label>
                  <Input
                    id="hero-image-url"
                    value={data.heroImageUrl}
                    placeholder="https://example.com/hero.jpg"
                    onChange={(e) => {
                      setHasTouchedContent(true);
                      setData("heroImageUrl", e.target.value);
                    }}
                    className="text-sm"
                  />
                  <p className="text-[10px] text-neutral-400">
                    Optional: shown as a hero image on the right side of the social preview
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Colors */}
          <div className="rounded-2xl border border-neutral-200 bg-white p-4 shadow-sm sm:p-5 dark:border-neutral-800 dark:bg-neutral-900">
            <h3 className="mb-3 text-sm font-semibold text-neutral-900 sm:mb-4 dark:text-white">Brand Colors</h3>
            {isExtractionLoading ? (
              <div className="space-y-3">
                <Skeleton className="h-10 w-full" />
                <Skeleton className="h-10 w-full" />
                <div className="flex items-center gap-2 text-xs text-neutral-400">
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  {extraction ? stageLabel(extraction.stage) : "Extracting colors…"}
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="space-y-1.5">
                    <Label className="text-xs" htmlFor="bg-color">
                      Background
                    </Label>
                    <div className="flex items-center gap-2">
                      <Input
                        type="color"
                        value={previewBg}
                        onChange={(e) => {
                          setHasTouchedColors(true);
                          setData("bgColorHex", e.target.value);
                        }}
                        className="h-9 w-12 cursor-pointer p-1"
                        aria-label="Background color picker"
                      />
                      <Input
                        id="bg-color"
                        value={data.bgColorHex}
                        placeholder="#0B1220"
                        onChange={(e) => {
                          setHasTouchedColors(true);
                          setData("bgColorHex", e.target.value);
                        }}
                        onBlur={() => setData("bgColorHex", normalizeHex(data.bgColorHex))}
                        className="font-mono text-xs"
                      />
                    </div>
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs" htmlFor="text-color">
                      Text
                    </Label>
                    <div className="flex items-center gap-2">
                      <Input
                        type="color"
                        value={previewText}
                        onChange={(e) => {
                          setHasTouchedColors(true);
                          setData("textColorHex", e.target.value);
                        }}
                        className="h-9 w-12 cursor-pointer p-1"
                        aria-label="Text color picker"
                      />
                      <Input
                        id="text-color"
                        value={data.textColorHex}
                        placeholder="#FFFFFF"
                        onChange={(e) => {
                          setHasTouchedColors(true);
                          setData("textColorHex", e.target.value);
                        }}
                        onBlur={() => setData("textColorHex", normalizeHex(data.textColorHex))}
                        className="font-mono text-xs"
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Logo */}
          <div className="rounded-2xl border border-neutral-200 bg-white p-4 shadow-sm sm:p-5 dark:border-neutral-800 dark:bg-neutral-900">
            <h3 className="mb-3 text-sm font-semibold text-neutral-900 sm:mb-4 dark:text-white">Logo</h3>
            {isExtractionLoading ? (
              <div className="space-y-3">
                <div className="grid grid-cols-3 gap-3">
                  <Skeleton className="aspect-square rounded-lg" />
                  <Skeleton className="aspect-square rounded-lg" />
                  <Skeleton className="aspect-square rounded-lg" />
                </div>
                <div className="flex items-center gap-2 text-xs text-neutral-400">
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  Finding logo options…
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                {extraction?.logoCandidates?.length ? (
                  <div className="grid grid-cols-3 gap-3">
                    {extraction.logoCandidates.map((candidate) => {
                      const selected = data.selectedLogoUrl === candidate.url;
                      return (
                        <button
                          key={candidate.url}
                          type="button"
                          onClick={() => handleLogoCandidateSelect(candidate.url)}
                          className={cn(
                            "flex aspect-square items-center justify-center rounded-xl border bg-white p-3 transition-all hover:border-orange-300 dark:bg-neutral-800",
                            selected
                              ? "border-orange-500 ring-2 ring-orange-200 dark:ring-orange-500/30"
                              : "border-neutral-200 dark:border-neutral-700",
                          )}
                        >
                          <img src={candidate.url} alt="Logo option" className="h-10 w-10 object-contain" />
                        </button>
                      );
                    })}
                  </div>
                ) : null}

                <div className="group cursor-pointer rounded-xl border-2 border-dashed border-neutral-200 p-4 text-center transition-colors hover:border-orange-300 dark:border-neutral-700">
                  <input type="file" onChange={handleLogoUpload} accept="image/*" className="hidden" id="logo-upload" />
                  <label htmlFor="logo-upload" className="block cursor-pointer">
                    <Upload className="mx-auto mb-1 h-5 w-5 text-neutral-400 transition-colors group-hover:text-orange-500" />
                    <p className="text-xs font-medium text-neutral-600 dark:text-neutral-300">
                      {uploadedLogoName || "Upload custom logo"}
                    </p>
                    <p className="text-[10px] text-neutral-400">PNG, JPG, SVG up to 5MB</p>
                  </label>
                </div>

                {uploadedLogoName && (
                  <div className="flex items-center justify-between gap-2 rounded-lg bg-neutral-50 p-2 dark:bg-neutral-800">
                    {uploadedLogoPreview && (
                      <img src={uploadedLogoPreview} alt="Uploaded" className="h-8 w-8 rounded object-contain" />
                    )}
                    <span className="flex-1 truncate text-xs">{uploadedLogoName}</span>
                    <button
                      type="button"
                      onClick={handleRemoveUpload}
                      className="rounded p-1 hover:bg-neutral-200 dark:hover:bg-neutral-700"
                    >
                      <X className="h-3.5 w-3.5 text-neutral-400" />
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Right column: Social feed mockup */}
        <div className="rounded-2xl border border-neutral-200 bg-white shadow-sm dark:border-neutral-800 dark:bg-neutral-900">
          <Tabs value={activeFeed} onValueChange={setActiveFeed} className="flex h-full flex-col">
            <div className="border-b border-neutral-100 px-3 pt-3 sm:px-4 sm:pt-4 dark:border-neutral-800">
              <TabsList className="w-full">
                <TabsTrigger value="x" className="flex-1 gap-1 text-[11px] sm:gap-1.5 sm:text-xs">
                  <XLogo className="h-3 w-3 sm:h-3.5 sm:w-3.5" />X
                </TabsTrigger>
                <TabsTrigger value="linkedin" className="flex-1 gap-1 text-[11px] sm:gap-1.5 sm:text-xs">
                  <LinkedInLogo className="h-3 w-3 sm:h-3.5 sm:w-3.5" />
                  LinkedIn
                </TabsTrigger>
                <TabsTrigger value="facebook" className="flex-1 gap-1 text-[11px] sm:gap-1.5 sm:text-xs">
                  <FacebookLogo className="h-3 w-3 sm:h-3.5 sm:w-3.5" />
                  Facebook
                </TabsTrigger>
              </TabsList>
            </div>

            <TabsContent value="x" className="flex-1 p-3 sm:p-4">
              <SocialFeedMockup
                platform="x"
                hostname={rootHost}
                previewImageUrl={mockupPreviewImageUrl}
                isLoading={isExtractionLoading}
                logoUrl={currentLogoUrl}
                bgColor={data.bgColorHex}
                textColor={data.textColorHex}
                pageTitle={data.ogTitle}
                pageDescription={data.ogDescription}
                heroImageUrl={data.heroImageUrl}
                onPostClick={handlePostClick}
              />
            </TabsContent>
            <TabsContent value="linkedin" className="flex-1 p-3 sm:p-4">
              <SocialFeedMockup
                platform="linkedin"
                hostname={rootHost}
                previewImageUrl={mockupPreviewImageUrl}
                isLoading={isExtractionLoading}
                logoUrl={currentLogoUrl}
                bgColor={data.bgColorHex}
                textColor={data.textColorHex}
                pageTitle={data.ogTitle}
                pageDescription={data.ogDescription}
                heroImageUrl={data.heroImageUrl}
                onPostClick={handlePostClick}
              />
            </TabsContent>
            <TabsContent value="facebook" className="flex-1 p-3 sm:p-4">
              <SocialFeedMockup
                platform="facebook"
                hostname={rootHost}
                previewImageUrl={mockupPreviewImageUrl}
                isLoading={isExtractionLoading}
                logoUrl={currentLogoUrl}
                bgColor={data.bgColorHex}
                textColor={data.textColorHex}
                pageTitle={data.ogTitle}
                pageDescription={data.ogDescription}
                heroImageUrl={data.heroImageUrl}
                onPostClick={handlePostClick}
              />
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </div>
  );
}

function XLogo({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="currentColor">
      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
    </svg>
  );
}

function LinkedInLogo({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="currentColor">
      <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
    </svg>
  );
}

function FacebookLogo({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="currentColor">
      <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
    </svg>
  );
}
